import React, { useEffect, useState, useRef } from 'react';
import { RescueEffect as RescueEffectType } from '../../utils/types';
import { RESCUE_ANIMATION_DURATIONS } from '../../utils/constants';
import { BurstParticle, particleEffectsService, TapParticle } from '../../services/particleEffectsService';
import TapParticles from '../common/TapParticles';
import BurstParticles from '../common/BurstParticles';

interface RescueEffectProps {
  effect: RescueEffectType;
  onEffectComplete?: () => void;
}

const RescueEffect: React.FC<RescueEffectProps> = ({ effect, onEffectComplete }) => {
  const [currentStage, setCurrentStage] = useState(effect.stage);
  const [isVisible, setIsVisible] = useState(true);
  const onEffectCompleteRef = useRef(onEffectComplete);

  // Update ref when callback changes
  useEffect(() => {
    onEffectCompleteRef.current = onEffectComplete;
  }, [onEffectComplete]);

  useEffect(() => {
    if (!effect.visible) {
      setIsVisible(false);
      return;
    }

    let timeoutId1: NodeJS.Timeout;
    let timeoutId2: NodeJS.Timeout;

    // Set up the complete animation sequence with effect-specific timing
    const effectId = effect.id || effect.timestamp;
    const startTime = performance.now();
    console.log(`Setting up rescue effect ${effectId}: starting with ${effect.stage} at time ${startTime}`);
    
    // Stage 1: UFO Break (800ms)
    timeoutId1 = setTimeout(() => {
      const transitionTime = performance.now();
      console.log(`Effect ${effectId}: transitioning to emoji_celebrate at time ${transitionTime} (${transitionTime - startTime}ms after start)`);
      setCurrentStage('emoji_celebrate');
    }, RESCUE_ANIMATION_DURATIONS.ufo_break);

    // Stage 2: Emoji Celebrate (1200ms after break starts)
    timeoutId2 = setTimeout(() => {
      const completionTime = performance.now();
      console.log(`Effect ${effectId}: completing and cleaning up at time ${completionTime} (${completionTime - startTime}ms after start)`);
      setIsVisible(false);
      onEffectCompleteRef.current?.();
    }, RESCUE_ANIMATION_DURATIONS.ufo_break + RESCUE_ANIMATION_DURATIONS.emoji_celebrate);

    return () => {
      if (timeoutId1) clearTimeout(timeoutId1);
      if (timeoutId2) clearTimeout(timeoutId2);
    };
  }, [effect.visible, effect.id, effect.timestamp]); // Use stable effect properties

  if (!isVisible) return null;

  const { x, y } = effect.position;
  const effectId = effect.id || effect.timestamp;
  
  console.log(`Rendering effect ${effectId} in stage: ${currentStage}`);

  return (
    <div style={{
      position: 'absolute',
      left: effect.position.x,
      top: effect.position.y,
      transform: 'translate(-50%, -50%)',
      zIndex: 20,
      pointerEvents: 'none',
      touchAction: 'none',
    }}>
      {currentStage === 'ufo_break' && (
        <UfoBreakEffect emoji={effect.emoji} position={effect.position} />
      )}
      {currentStage === 'emoji_celebrate' && (
        <EmojiCelebrateEffect emoji={effect.emoji} />
      )}
    </div>
  );
};

const UfoBreakEffect: React.FC<{ emoji: string, position: { x: number, y: number } }> = ({ emoji, position }) => {
  const [particles, setParticles] = useState<{tap: TapParticle[], burst: BurstParticle[]}>({tap: [], burst: []});
  const [effectType, setEffectType] = useState<'tap' | 'burst' | 'both'>('tap');
  const requestRef = useRef<number | null>(null);
  const CONTAINER_SIZE = 500;

  // Randomly choose between tap, burst, or both effects
  useEffect(() => {
    const random = Math.random();
    if (random < 0.4) {
      setEffectType('tap');
    } else if (random < 0.7) {
      setEffectType('burst');
    } else {
      setEffectType('both');
    }
  }, []);

  // Initialize particles at the center of the local container
  useEffect(() => {
    const newParticles: {tap: TapParticle[], burst: BurstParticle[]} = {tap: [], burst: []};
    const normX = 0.5;
    const normY = 0.5;
    if (effectType === 'tap' || effectType === 'both') {
      newParticles.tap = particleEffectsService.createTapParticles(normX, normY, 2.0);
    }
    if (effectType === 'burst' || effectType === 'both') {
      newParticles.burst = particleEffectsService.createBurstParticles(normX, normY);
    }
    setParticles(newParticles);
  }, [effectType]);

  // Animate particles
  useEffect(() => {
    const animate = () => {
      setParticles(prev => ({
        tap: particleEffectsService.updateTapParticles(prev.tap),
        burst: particleEffectsService.updateBurstParticles(prev.burst)
      }));
      if (particles.tap.length > 0 || particles.burst.length > 0) {
        requestRef.current = requestAnimationFrame(animate);
      }
    };
    if (particles.tap.length > 0 || particles.burst.length > 0) {
      requestRef.current = requestAnimationFrame(animate);
    }
    return () => {
      if (requestRef.current) {
        cancelAnimationFrame(requestRef.current);
      }
    };
  }, [particles.tap.length, particles.burst.length]);

  // Render a local container centered at the tap position
  return (
    <div style={{
      position: 'absolute',
      left: -CONTAINER_SIZE/2,
      top: -CONTAINER_SIZE/2,
      width: CONTAINER_SIZE,
      height: CONTAINER_SIZE,
      pointerEvents: 'none',
    }}>
      <TapParticles particles={particles.tap} containerSize={CONTAINER_SIZE} zIndex={5} />
      <BurstParticles particles={particles.burst} containerSize={CONTAINER_SIZE} zIndex={4} />
      {/* Optional: Keep a small explosion emoji for visual impact */}
      <div style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        fontSize: '2em',
        animation: 'quickExplosion 0.6s ease-out forwards',
        color: '#FF6B35',
        zIndex: 3
      }}>
        💥
      </div>
      <style>
        {`
          @keyframes quickExplosion {
            0% { transform: translate(-50%, -50%) scale(0.2); opacity: 0; }
            30% { transform: translate(-50%, -50%) scale(1.2); opacity: 1; }
            100% { transform: translate(-50%, -50%) scale(2); opacity: 0; }
          }
        `}
      </style>
    </div>
  );
};



const EmojiCelebrateEffect: React.FC<{ emoji: string }> = ({ emoji }) => {
  return (
    <div style={{ position: 'relative' }}>
      {/* Celebrating Emoji */}
      <div style={{
        fontSize: '3.5em',
        animation: 'emojiCelebrate 1s ease-in-out forwards',
      }}>
        {emoji}
      </div>
      
      {/* Rescue Success Text */}
      <div style={{
        position: 'absolute',
        top: '-40px',
        left: '50%',
        transform: 'translateX(-50%)',
        fontSize: '1.2em',
        fontWeight: 'bold',
        color: '#4CAF50',
        textShadow: '0 0 10px rgba(76, 175, 80, 0.8)',
        animation: 'rescueText 1s ease-out forwards',
        whiteSpace: 'nowrap'
      }}>
        RESCUED!
      </div>
      
      {/* Victory Particles */}
      <div style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        fontSize: '1em',
        animation: 'victoryParticles 1s ease-out forwards'
      }}>
        🌟⭐✨🎊🎉
      </div>

      <style>
        {`
          @keyframes emojiCelebrate {
            0% { 
              transform: scale(1) rotate(0deg) translateY(-10px); 
              filter: brightness(1);
            }
            25% { 
              transform: scale(1.2) rotate(-10deg) translateY(-15px); 
              filter: brightness(1.3);
            }
            50% { 
              transform: scale(1.3) rotate(10deg) translateY(-20px); 
              filter: brightness(1.5);
            }
            75% { 
              transform: scale(1.2) rotate(-5deg) translateY(-15px); 
              filter: brightness(1.3);
            }
            100% { 
              transform: scale(0.8) rotate(0deg) translateY(-80px); 
              filter: brightness(1);
              opacity: 0;
            }
          }
          
          @keyframes rescueText {
            0% { transform: translateX(-50%) translateY(10px) scale(0); opacity: 0; }
            30% { transform: translateX(-50%) translateY(0px) scale(1.2); opacity: 1; }
            70% { transform: translateX(-50%) translateY(0px) scale(1); opacity: 1; }
            100% { transform: translateX(-50%) translateY(-20px) scale(0.8); opacity: 0; }
          }
          
          @keyframes victoryParticles {
            0% { transform: translate(-50%, -50%) scale(0); opacity: 1; }
            50% { transform: translate(-50%, -50%) scale(1.5) rotate(180deg); opacity: 0.8; }
            100% { transform: translate(-50%, -50%) scale(3) rotate(360deg); opacity: 0; }
          }
        `}
      </style>
    </div>
  );
};

export default RescueEffect; 